// ==========================================
// Buddy workflows — palette, runs and LIVE step check-off (3d INTERACTION FLOW)
// ==========================================
// Chips run with defaults, "/" filters inline, and execution renders as an inline
// step card. A run is ASYNCHRONOUS: we start it, then POLL the backend for real
// progress and tick the card from that. When the run hits an approval step it pauses
// and the card shows Yes/No; the answer resumes it. Failures flip BB to `oops`.
// ==========================================

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  Workflow,
  WorkflowRun,
  WorkflowRunStatus,
  WorkflowStep,
  WorkflowStepStatus,
} from '../models/buddy.types';
import { CHIP_RAIL_VISIBLE } from '../models/buddy.constants';
import { BbStateService } from './bb-state.service';
import { ApiRunState, BuddyApiService } from './buddy-api.service';
import { BuddyChatService } from './buddy-chat.service';
import { BuddyProgressService } from './buddy-progress.service';
import { BuddyThreadsService } from './buddy-threads.service';

/** How often we poll a running workflow for progress. */
const POLL_INTERVAL_MS = 1500;

let runCounter = 0;

@Injectable({ providedIn: 'root' })
export class BuddyWorkflowService implements OnDestroy {
  /** Poll loop per run (keyed by the local UI run id). */
  private readonly pollers = new Map<string, ReturnType<typeof setInterval>>();
  /** Local UI run id → the backend run uuid it maps to. */
  private readonly backendIds = new Map<string, string>();
  /** Links a run back to the chat message hosting its card, so XP lands in the right place. */
  private readonly runMessages = new Map<string, string>();

  /**
   * Runs belong to their thread, so switching conversations swaps the visible cards
   * along with the messages that host them.
   */
  readonly runs: Observable<Record<string, WorkflowRun>>;

  /** The palette workflows, loaded from GET /buddy/workflows (was MOCK_WORKFLOWS). */
  private allWorkflows: Workflow[] = [];
  private readonly workflowsSubject = new BehaviorSubject<readonly Workflow[]>([]);
  /** Emits when the workflow set (re)loads, so OnPush surfaces can markForCheck. */
  readonly workflows$ = this.workflowsSubject.asObservable();

  get all(): readonly Workflow[] {
    return this.allWorkflows;
  }

  constructor(
    private readonly bb: BbStateService,
    private readonly chat: BuddyChatService,
    private readonly progress: BuddyProgressService,
    private readonly threads: BuddyThreadsService,
    private readonly api: BuddyApiService
  ) {
    this.runs = this.threads.activeThread.pipe(map((t) => t.runs));

    // Load the palette from the backend: system defaults + this office's own.
    // pinned workflows take the chip-rail slots; the rest sit behind the "+N" counter.
    this.api.listWorkflows().subscribe((workflows) => {
      this.allWorkflows = workflows;
      this.workflowsSubject.next(workflows);
    });
  }

  // ------------------------------------------
  // Palette
  // ------------------------------------------

  /**
   * The chip-rail workflows — the current user's MOST-USED (by run count), up to
   * CHIP_RAIL_VISIBLE. The backend already returns `all` ordered by usage (then system
   * defaults, then a stable fallback), so the rail is simply the first few.
   */
  get railWorkflows(): readonly Workflow[] {
    return this.all.slice(0, CHIP_RAIL_VISIBLE);
  }

  /** Everything the rail collapses behind its "+N" counter. */
  get overflowCount(): number {
    return this.all.length - this.railWorkflows.length;
  }

  /** Backs both the "/" inline filter and the palette's search field. */
  filter(query: string): readonly Workflow[] {
    const q = query.trim().toLowerCase().replace(/^\//, '');
    if (!q) {
      return this.all;
    }
    return this.all.filter(
      (w) => w.name.toLowerCase().includes(q) || w.description.toLowerCase().includes(q)
    );
  }

  byId(id: string): Workflow | undefined {
    return this.all.find((w) => w.id === id);
  }

  runById(runId: string): WorkflowRun | undefined {
    return this.threads.active.runs[runId];
  }

  /** A run's own thread, which is where every one of its updates is written. */
  private lookup(runId: string): WorkflowRun | undefined {
    for (const thread of this.threads.all) {
      const run = thread.runs[runId];
      if (run) {
        return run;
      }
    }
    return undefined;
  }

  // ------------------------------------------
  // Execution
  // ------------------------------------------

  /**
   * Start a workflow. Renders the step card immediately, then fires the async run at the
   * backend and polls it for real progress. Returns the local run id.
   */
  run(workflowId: string): string | null {
    const workflow = this.byId(workflowId);
    if (!workflow) {
      return null;
    }

    runCounter += 1;
    const runId = `run-${runCounter}`;
    // Pin the thread up front so a run that finishes after you switch away still
    // writes its steps and XP into the conversation it belongs to.
    const threadId = this.threads.activeThreadId;
    const steps: WorkflowStep[] = workflow.stepLabels.map((label, i) => ({
      id: `${runId}-s${i}`,
      label,
      status: 'pending',
      log: [],
    }));

    const run: WorkflowRun = {
      id: runId,
      threadId,
      workflowId,
      name: workflow.name,
      status: 'running',
      steps,
      startedAt: Date.now(),
      error: null,
      approval: null,
      summary: null,
    };

    this.commit(run);
    const messageId = this.chat.attachRun(runId, `Running \`${workflow.name}\`…`, threadId);
    this.runMessages.set(runId, messageId);

    // BB holds `thinking` until the run resolves.
    this.bb.wake();
    this.bb.think();

    // Start the real async run; the engine works in the background and we poll it.
    this.api.startWorkflowRun(workflowId).subscribe({
      next: (state) => {
        this.backendIds.set(runId, state.id);
        this.applyState(runId, state);
        if (this.shouldPoll(state.status)) {
          this.startPolling(runId);
        }
      },
      error: () => {
        this.failRun(runId, 'The automation engine is unavailable right now. Please try again.');
      },
    });

    return runId;
  }

  /** The user's Yes/No on a paused (awaiting_approval) run. Resumes the engine. */
  decide(runId: string, approved: boolean): void {
    const run = this.lookup(runId);
    if (!run || run.status !== 'awaiting_approval') {
      return;
    }
    const backendId = this.backendIds.get(runId);
    if (!backendId) {
      return;
    }

    // Optimistically clear the prompt and show the run moving again.
    this.commit({ ...run, status: 'running', approval: null });
    this.bb.think();

    this.api.decideRun(backendId, approved).subscribe({
      next: (state) => {
        this.applyState(runId, state);
        if (this.shouldPoll(state.status)) {
          this.startPolling(runId);
        }
      },
      error: () => {
        // Put the prompt back so the user can answer again.
        const live = this.lookup(runId);
        if (live) {
          this.commit({ ...live, status: 'awaiting_approval', approval: run.approval });
        }
      },
    });
  }

  /** Stop watching a run locally (there is no server-side cancel yet). */
  cancel(runId: string): void {
    const run = this.lookup(runId);
    if (!run || (run.status !== 'running' && run.status !== 'awaiting_approval')) {
      return;
    }
    this.stopPolling(runId);
    this.commit({
      ...run,
      status: 'cancelled',
      approval: null,
      steps: run.steps.map((s) => (s.status === 'running' ? { ...s, status: 'pending' } : s)),
    });
    this.chat.appendSystem(`\`${run.name}\` cancelled.`, run.threadId);
    this.bb.settle();
  }

  /** Offered by the failure card alongside "edit". */
  retry(runId: string): string | null {
    const run = this.lookup(runId);
    if (!run) {
      return null;
    }
    return this.run(run.workflowId);
  }

  // ------------------------------------------
  // Internals — polling + state mapping
  // ------------------------------------------

  private startPolling(runId: string): void {
    if (this.pollers.has(runId)) {
      return;
    }
    const backendId = this.backendIds.get(runId);
    if (!backendId) {
      return;
    }
    const timer = setInterval(() => {
      this.api.pollRun(backendId).subscribe({
        next: (state) => this.applyState(runId, state),
        error: () => {
          /* transient poll failure — keep the loop, try again next tick */
        },
      });
    }, POLL_INTERVAL_MS);
    this.pollers.set(runId, timer);
  }

  private stopPolling(runId: string): void {
    const timer = this.pollers.get(runId);
    if (timer !== undefined) {
      clearInterval(timer);
      this.pollers.delete(runId);
    }
  }

  /** Fold a polled backend state into the visible run card. */
  private applyState(runId: string, state: ApiRunState): void {
    const run = this.lookup(runId);
    if (!run) {
      return;
    }

    const uiStatus = this.mapStatus(state.status);
    this.commit({
      ...run,
      status: uiStatus,
      steps: this.mapSteps(run, state),
      approval: state.approval
        ? {
            title: state.approval.title,
            message: state.approval.message,
            count: state.approval.count,
            items: state.approval.items,
            confirmLabel: state.approval.confirmLabel,
            cancelLabel: state.approval.cancelLabel,
          }
        : null,
      summary: state.summary,
      error: state.error,
    });

    if (uiStatus === 'done') {
      this.stopPolling(runId);
      if (state.summary) {
        this.chat.appendSystem(state.summary, run.threadId);
      }
      this.settleSuccess(run.name, runId, run.threadId);
    } else if (uiStatus === 'failed') {
      this.stopPolling(runId);
      this.bb.fail();
    } else if (uiStatus === 'awaiting_approval') {
      // Hold here until the user answers — no point polling an idle pause.
      this.stopPolling(runId);
      this.bb.settle();
    }
  }

  private mapStatus(status: ApiRunState['status']): WorkflowRunStatus {
    switch (status) {
      case 'completed':
        return 'done';
      case 'failed':
        return 'failed';
      case 'awaiting_approval':
        return 'awaiting_approval';
      default:
        return 'running';
    }
  }

  private mapSteps(run: WorkflowRun, state: ApiRunState): WorkflowStep[] {
    return run.steps.map((step, i) => {
      const point = state.progress[i];
      if (!point) {
        return step;
      }
      return {
        ...step,
        status: this.mapStepState(point.state),
        log: point.detail ? [point.detail] : step.log,
      };
    });
  }

  private mapStepState(state: string): WorkflowStepStatus {
    switch (state) {
      case 'active':
        return 'running';
      case 'done':
        return 'done';
      case 'failed':
        return 'failed';
      default:
        return 'pending';
    }
  }

  /** Only active runs are worth polling — not a paused approval or a finished run. */
  private shouldPoll(status: ApiRunState['status']): boolean {
    return status === 'running' || status === 'queued';
  }

  private failRun(runId: string, message: string): void {
    const run = this.lookup(runId);
    if (!run) {
      return;
    }
    this.stopPolling(runId);
    this.commit({ ...run, status: 'failed', approval: null, error: message });
    this.bb.fail();
  }

  private settleSuccess(name: string, runId: string, threadId: string): void {
    const award = this.progress.awardWorkflowRun(name);

    const messageId = this.runMessages.get(runId);
    if (messageId) {
      this.chat.setReward(messageId, award.reward, threadId);
    }

    if (award.leveledUp) {
      // happy ──milestone──► celebratory
      this.bb.celebrate();
      this.chat.appendMilestone(`Level ${award.newLevel} — BB grows into it.`, threadId);
    } else {
      this.bb.succeed();
    }
    this.bb.noteActivity();
  }

  private commit(run: WorkflowRun): void {
    const thread = this.threads.byId(run.threadId);
    if (!thread) {
      return;
    }
    this.threads.setRuns(run.threadId, { ...thread.runs, [run.id]: run });
  }

  ngOnDestroy(): void {
    this.pollers.forEach((t) => clearInterval(t));
    this.pollers.clear();
  }
}
